import { NavLink, Link, useLocation } from "react-router-dom";
import { useEffect, useState, useRef } from "react";
import { Menu, X, ChevronDown } from "lucide-react";
import AshrenLogo from "@/components/AshrenLogo";

const serviceLinks = [
  { to: "/technology-staffing", label: "Technology Staffing", desc: "AI/ML, Cloud, Cyber, Quantum" },
  { to: "/general-staffing", label: "General Staffing", desc: "Finance, HR, Legal, Sales" },
  { to: "/healthcare-staffing", label: "Healthcare Staffing", desc: "Travel Nursing, Allied, Non-Clinical" },
  { to: "/rpo", label: "RPO", desc: "Recruitment Process Outsourcing" },
  { to: "/eor", label: "EOR", desc: "Employer of Record — Canada" },
];

const otherLinks = [
  { to: "/for-employers", label: "For Employers" },
  { to: "/for-candidates", label: "For Candidates" },
  { to: "/resources", label: "Resources" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [scrolled, setScrolled] = useState(
    () => typeof window !== "undefined" && window.scrollY > 4,
  );
  const location = useLocation();
  const servicesRef = useRef(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 4);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const onClickAway = (e) => {
      if (servicesRef.current && !servicesRef.current.contains(e.target)) {
        setServicesOpen(false);
      }
    };
    document.addEventListener("mousedown", onClickAway);
    return () => document.removeEventListener("mousedown", onClickAway);
  }, []);

  const closeMobile = () => setOpen(false);
  const closeServices = () => setServicesOpen(false);
  const isServicesActive = serviceLinks.some((l) => location.pathname === l.to);

  return (
    <header
      data-testid="site-navbar"
      className={`sticky top-0 z-50 transition-all ${
        scrolled
          ? "bg-[color:var(--canvas-light)]/90 backdrop-blur-xl border-b border-[color:var(--border)]"
          : "bg-[color:var(--canvas-light)] border-b border-transparent"
      }`}
    >
      <div className="max-w-[1280px] mx-auto px-5 md:px-10 lg:px-14 flex items-center justify-between h-[72px]">
        <Link to="/" className="flex items-center gap-3 shrink-0" data-testid="nav-logo-link">
          <AshrenLogo variant="dark" className="h-11 w-auto" testId="nav-ashren-logo" />
        </Link>

        <nav className="hidden lg:flex items-center gap-6" data-testid="nav-desktop-menu">
          {/* Services dropdown */}
          <div className="relative" ref={servicesRef}>
            <button
              type="button"
              onClick={() => setServicesOpen((s) => !s)}
              className={`nav-link flex items-center gap-1 ${isServicesActive ? "active" : ""}`}
              data-testid="nav-services-toggle"
              aria-expanded={servicesOpen}
              aria-haspopup="true"
            >
              Services
              <ChevronDown size={14} className={`transition-transform ${servicesOpen ? "rotate-180" : ""}`} />
            </button>
            {servicesOpen && (
              <div
                className="absolute top-full left-0 mt-3 w-[340px] bg-white border border-[color:var(--border)] rounded-xl shadow-xl p-2 z-50"
                data-testid="nav-services-dropdown"
              >
                {serviceLinks.map((l) => (
                  <NavLink
                    key={l.to}
                    to={l.to}
                    onClick={closeServices}
                    className={({ isActive }) =>
                      `block px-4 py-3 rounded-lg hover:bg-[color:var(--canvas-light)] transition-colors ${isActive ? "bg-[color:var(--canvas-light)]" : ""}`
                    }
                    data-testid={`nav-services-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                  >
                    <div className="font-medium text-[color:var(--primary-dark)] text-[15px]">{l.label}</div>
                    <div className="text-xs text-[color:var(--muted)] mt-0.5">{l.desc}</div>
                  </NavLink>
                ))}
              </div>
            )}
          </div>

          {otherLinks.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center">
          <Link
            to="/for-employers"
            className="btn-primary"
            data-testid="nav-hire-talent-btn"
          >
            Hire Talent
          </Link>
        </div>

        <button
          type="button"
          className="lg:hidden p-2 rounded-md hover:bg-gray-100"
          onClick={() => setOpen((s) => !s)}
          data-testid="nav-mobile-toggle"
          aria-label="Toggle menu"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {open && (
        <div
          className="lg:hidden bg-[color:var(--canvas-light)] border-t border-[color:var(--border)] px-5 md:px-10 py-5 max-h-[calc(100vh-72px)] overflow-y-auto"
          data-testid="nav-mobile-menu"
        >
          <div className="flex flex-col gap-4">
            <div className="text-xs font-semibold uppercase tracking-widest text-[color:var(--muted)]">Services</div>
            {serviceLinks.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                onClick={closeMobile}
                className={({ isActive }) =>
                  `nav-link ${isActive ? "active" : ""}`
                }
                data-testid={`nav-mobile-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              >
                {l.label}
              </NavLink>
            ))}
            <div className="text-xs font-semibold uppercase tracking-widest text-[color:var(--muted)] pt-2">Company</div>
            {otherLinks.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                onClick={closeMobile}
                className={({ isActive }) =>
                  `nav-link ${isActive ? "active" : ""}`
                }
                data-testid={`nav-mobile-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              >
                {l.label}
              </NavLink>
            ))}
            <Link
              to="/for-employers"
              onClick={closeMobile}
              className="btn-primary mt-2 self-start"
              data-testid="nav-mobile-hire-talent-btn"
            >
              Hire Talent
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
